import axios from "axios";

/**
 * Fetch the current session from the cookie-based `/auth/me` endpoint.
 *
 * Authentication is owned by the external auth portal, which sets a `.foo.com`
 * session cookie. The browser sends that cookie automatically (`withCredentials`),
 * so this requires no token handling on the frontend. an-api validates the
 * cookie and returns the user identity + groups.
 *
 * @returns {Promise<{ email: string, groups: string[], session: object } | null>}
 *   normalized session info, or null if unauthenticated / on error.
 */
export async function fetchSession() {
  return (await fetchSessionStatus()).result;
}

/**
 * Like `fetchSession`, but also reports whether `/auth/me` definitively
 * rejected the session (401) as opposed to failing transiently (network, 5xx).
 *
 * @returns {Promise<{
 *   result: { email: string, groups: string[], session: object } | null,
 *   unauthorized: boolean,
 * }>}
 */
export async function fetchSessionStatus() {
  try {
    const response = await axios.get(`${process.env.BASE_URL}/auth/me`, {
      withCredentials: true,
      validateStatus: () => true,
    });

    const unauthorized = response.status === 401;
    if (response.status !== 200 || !response.data) return { result: null, unauthorized };

    // an-api returns identity under `session` (email, groups, user_id, expiry, ...).
    const session = response.data.session || response.data;
    if (!session || (!session.email && !session.user_id)) {
      return { result: null, unauthorized: false };
    }

    return {
      result: {
        email: session.email,
        groups: session.groups || [],
        session,
      },
      unauthorized: false,
    };
  } catch (e) {
    console.error("fetchSession /auth/me failed", e);
    return { result: null, unauthorized: false };
  }
}

/**
 * Terminate the server session via the cookie-based `/auth/logout` endpoint.
 * No token handling — the `.foo.com` session cookie is sent automatically and
 * cleared server-side. Resolves regardless of outcome so callers can proceed
 * to redirect the user to the auth portal.
 */
export async function logout() {
  try {
    await axios.post(`${process.env.BASE_URL}/auth/logout`, {}, { withCredentials: true });
  } catch (e) {
    console.error("logout /auth/logout failed", e);
  }
}
